<template>
    <FormulateForm v-model="formData" @submit="submit" autocomplete="off" class="boka-operation">
        <div class="patient-dialog-container">
            <DialogAktivPatient :patient="patient" />
        </div>
        <div class="form-body">
            <div class="row">
                <KFormFieldDisplay label="Typoperation / Operationskod" name="typoperation" :value="typopOrOpkod" />
                <div class="prelbok-locked" v-if="preliminarbokningIsLocked">
                    <img src="@/assets/preliminarbokning.svg" />
                    <span>Preliminärbokning</span>
                </div>
                <FormulateInput
                    v-else
                    type="checkbox"
                    name="preliminarbokning"
                    label="Preliminärbokning"
                    :disabled="params?.listItem?.status === 'Bokad' || operationsanmalan.status === 'Bokad'"
                    @input="setPreliminarbokning"
                />
            </div>
            <div class="row">
                <FormulateInput
                    type="datepicker"
                    label="Operationsdatum *"
                    name="datum"
                    validation="required"
                    @input="setInstallelsedatum"
                />
                <FormulateInput type="timepicker" useNow label="Operationstid" name="tid" />
            </div>
            <div class="row">
                <FormulateInput type="vueselect" label="Sal" name="sal" :options="salar" />
            </div>
            <div class="row">
                <FormulateInput
                    type="vueselect"
                    :label="`Operatör${!formData.bokad_huvudoperator_ej_bestamd ? ' *' : ''}`"
                    name="huvudoperator"
                    :options="operatorer"
                    :validation="!formData.bokad_huvudoperator_ej_bestamd ? 'required' : ''"
                    :disabled="formData.bokad_huvudoperator_ej_bestamd"
                />
                <FormulateInput
                    type="checkbox"
                    name="bokad_huvudoperator_ej_bestamd"
                    label="Operatör ej bestämd"
                    @input="setEjBestamd"
                />
            </div>
            <div class="row">
                <FormulateInput type="datepicker" label="Inställelsedatum" name="installelsedatum" />
                <FormulateInput type="timepicker" useNow label="Inställelsetid" name="installelsetid" />
            </div>
        </div>
        <ButtonGroup>
            <Button type="submit" :disabled="!preliminarbokning && formData.bokad_huvudoperator_ej_bestamd">
                <span>{{ uppdaterar ? "Redigera bokning" : "Boka operation" }}</span>
            </Button>
            <Button
                type="button"
                size="secondary"
                @click="submitAndSkapaKallelse()"
                :disabled="!preliminarbokning && formData.bokad_huvudoperator_ej_bestamd"
            >
                <span>{{ uppdaterar ? "Redigera bokning" : "Boka operation" }} och skapa kallelse</span>
            </Button>
            <Button type="button" variant="contour" size="secondary" @click="cancel()">Avbryt</Button>
        </ButtonGroup>
        <KlinikenPopup
            v-if="showpopupmodal"
            :popupProp="showpopupmodal"
            typofPopup="warning"
            @cancelPopup="cancelPopup"
            @closeAndReturnTrue="tvingaBokning"
            componentName="boka operation"
            :popupMessage="popupFields"
        >
        </KlinikenPopup>
    </FormulateForm>
</template>
<script>
    import moment from "moment/moment";
    import dialogEvents from "@/dialogEvents";
    import { mapActions, mapGetters } from "vuex";
    import Button from "@/components/Button.vue";
    import { getHourMinuteFromTime, getNullIfEmpty, salarOptions } from "@/tabs/Operation/utils";
    import { preliminarbokningLocked } from "@/tabs/Operation/utilsTs";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import KlinikenPopup from "@/components/Popup/KlinikenPopup.vue";
    import { getConfig, klinikenApi } from "@/api";
    import KallelseSend from "@/tabs/Operation/Kallelser/KallelseSend.vue";
    import DialogAktivPatient from "./DialogAktivPatient.vue";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";

    export default {
        name: "BokaOperation",
        components: { Button, ButtonGroup, KlinikenPopup, DialogAktivPatient, KFormFieldDisplay },
        props: {
            value: {
                type: Object,
                required: false,
            },
        },
        data() {
            return {
                params: this.$attrs.response,
                operationsanmalan: {},
                patient: {},
                salar: [],
                operatorer: [],
                uppdaterar: false,
                formData: {
                    datum: "",
                    tid: "",
                    sal: "",
                    huvudoperator: "",
                    installelsedatum: "",
                    installelsetid: "",
                    preliminarbokning: false,
                    bokad_huvudoperator_ej_bestamd: false,
                },
                showpopupmodal: false,
                payload: {},
                extra_payload: {},
                popupFields: {},
                skapaKallelse: false,
            };
        },
        computed: {
            ...mapGetters("operation/operationsanmalan", ["getAlreadyBooked"]),

            typopOrOpkod() {
                return (
                    this.operationsanmalan.typoperation_display?.namn ??
                    `${this.operationsanmalan.huvudoperationskod_display?.code} ${this.operationsanmalan.huvudoperationskod_display?.displayName}`
                );
            },

            preliminarbokningIsLocked() {
                // I vissa vägar in till BokaOperaton skickas params in, och då laddas preliminarbokningIsLocked utan fördröjning.
                // Men vi behöver en fallback för när man inte har skickat in params.
                return this.params?.preliminarbokningLocked ?? preliminarbokningLocked(this.operationsanmalan);
            },

            preliminarbokning() {
                return this.preliminarbokningIsLocked || this.formData.preliminarbokning;
            },
        },
        methods: {
            ...mapActions("operation/operationsanmalan", ["bokaOpAnmalan", "resetTvingadBokning"]),
            cancelPopup() {
                this.showpopupmodal = false;
                this.resetTvingadBokning();
            },
            async tvingaBokning() {
                if (this.getAlreadyBooked.retroaktiv_bokning) {
                    this.extra_payload = { ...this.extra_payload, retroaktiv_bokning: true };
                } else {
                    this.extra_payload = { ...this.extra_payload, overboka: true };
                }
                this.showpopupmodal = false;
                this.resetTvingadBokning();
                await this.boka();
            },
            setInstallelsedatum() {
                // Beräknar inställelsedatum enligt opdatum minus inläggning antal dagar före

                let suggested_installelsedatum = this.formData.datum;

                if (suggested_installelsedatum && this.operationsanmalan.inlaggning_antal_dagar_fore) {
                    suggested_installelsedatum = moment(suggested_installelsedatum)
                        .subtract(this.operationsanmalan.inlaggning_antal_dagar_fore, "days")
                        .format("YYYY-MM-DD");
                }

                this.formData.installelsedatum = suggested_installelsedatum;
            },
            setEjBestamd(value) {
                if (value) {
                    this.formData.huvudoperator = null;
                }
            },
            setPreliminarbokning(value) {
                if (!value) {
                    this.formData.bokad_huvudoperator_ej_bestamd = false;
                }
            },
            cancel() {
                dialogEvents.$emit("closePopup");
                this.$emit("closePopup");
                this.$emit("close");
            },
            async submit() {
                this.skapaKallelse = false;
                await this.boka();
            },
            async openKallelse({ receiver }) {
                await this.$store.dispatch("tabs/closeTab", KallelseSend, { root: true });
                await this.$store.dispatch(
                    "tabs/openTab",
                    {
                        component: KallelseSend,
                        parameters: {
                            receiver,
                            baseUrl: "/operation/anmalan/",
                            path: "/kallelse/",
                        },
                    },
                    { root: true }
                );
            },
            async boka() {
                this.payload = {
                    id: this.operationsanmalan.id,
                    bokad_operation_datum: getNullIfEmpty(this.formData.datum),
                    bokad_operation_tid: getNullIfEmpty(this.formData.tid),
                    bokad_sal: this.formData.sal,
                    bokad_huvudoperator: this.formData.huvudoperator,
                    bokad_huvudoperator_ej_bestamd: this.formData.bokad_huvudoperator_ej_bestamd,
                    installelsedatum: getNullIfEmpty(this.formData.installelsedatum),
                    installelsetid: getNullIfEmpty(this.formData.installelsetid),
                    preliminarbokning: this.preliminarbokning,
                    ...this.extra_payload,
                };

                const anmalan = await this.bokaOpAnmalan({ payload: this.payload });

                if (this.getAlreadyBooked?.errorList?.length) {
                    this.popupFields = {
                        message: this.getAlreadyBooked.retroaktiv_bokning
                            ? this.getAlreadyBooked.errorList[0]
                            : "Operationsresurser som du vill använda är redan bokade på denna tid.",
                        buttonsTitles: {
                            cancel: "Avbryt",
                            action: "Gå vidare med bokning ändå",
                        },
                        typofAction: "donotremoveTab",
                        errorList: this.getAlreadyBooked.overbokad ? this.getAlreadyBooked.errorList : [],
                    };
                    this.showpopupmodal = true;
                } else if (anmalan && this.skapaKallelse) {
                    await this.openKallelse({ receiver: anmalan });
                }

                if (!this.showpopupmodal) {
                    this.resetTvingadBokning();
                    this.cancel();
                }
            },
            async submitAndSkapaKallelse() {
                this.skapaKallelse = true;
                await this.boka();
            },
        },

        async created() {
            const lista_personal = await klinikenApi.get(
                "/core/profiles/",
                getConfig({
                    params: { roll__code: "kirurg" },
                })
            );

            this.operationsanmalan = this.params?.listItem ?? this.value;
            const response = await klinikenApi.get(
                "/operation/salar/",
                getConfig({
                    params: { orgunit: this.operationsanmalan.operationsenhet },
                })
            );
            this.salar = salarOptions(response.data.results);

            this.uppdaterar = this.operationsanmalan.status === "Bokad" || this.operationsanmalan.preliminarbokning;

            this.patient = { ...this.operationsanmalan.patient_display };

            this.formData.datum = this.operationsanmalan.current_bokad_operation_datum ?? "";
            this.formData.tid = this.operationsanmalan.current_bokad_operation_tid
                ? getHourMinuteFromTime(this.operationsanmalan.current_bokad_operation_tid)
                : "";

            this.formData.sal = this.operationsanmalan.current_bokad_sal
                ? `${this.operationsanmalan.current_bokad_sal}`
                : "";
            this.formData.huvudoperator = this.operationsanmalan.current_bokad_huvudoperator ?? "";
            this.formData.bokad_huvudoperator_ej_bestamd =
                this.operationsanmalan.current_bokad_huvudoperator_ej_bestamd;

            this.formData.installelsedatum = this.operationsanmalan.installelsedatum ?? "";
            this.formData.installelsetid = this.operationsanmalan.installelsetid
                ? getHourMinuteFromTime(this.operationsanmalan.installelsetid)
                : "";

            this.operatorer = lista_personal.data.results
                .filter((personal) => {
                    return (
                        (personal.is_active === true &&
                            personal.orgUnit?.id === this.operationsanmalan.operationsenhet) ||
                        this.formData.huvudoperator === personal.pk
                    );
                })
                .map((personal) => {
                    return {
                        value: personal.pk,
                        label: personal.user_display.name,
                    };
                });

            if (!this.formData.installelsedatum) {
                this.setInstallelsedatum();
            }

            this.formData.preliminarbokning = this.operationsanmalan.preliminarbokning;
        },
    };
</script>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/modal.scss";

    .boka-operation {
        padding: 0 50px;

        .patient-dialog-container {
            margin-bottom: 28px;
        }

        .form-body {
            margin-top: 40px;
            margin-bottom: 40px;
            display: flex;
            flex-direction: column;
            gap: 20px;

            .row {
                display: flex;
                justify-content: space-between;
                gap: 20px;

                > * {
                    flex: 1 1 0;
                }
            }

            .formulate-input {
                color: $color-label-grey;
                width: 47.7%;
                height: 71px;
            }
        }

        .prelbok-locked {
            img {
                margin-right: 6px;
                margin-bottom: 6px;
            }
        }
    }
</style>
